/**
 * HTML 블록 요소 추출 결과
 */
export type HtmlBlockExtractionResult = {
  /** 추출된 첫 번째 블록 요소의 HTML 문자열 */
  firstBlock: string;
  /** 첫 번째 블록 이후 추가 컨텐츠 존재 여부 */
  hasMore: boolean;
};

const MAX_BLOCK_LENGTH = 60;

/**
 * HTML 문자열에서 모든 태그를 제거하고 순수 텍스트만 추출합니다
 *
 * @description
 * DOMParser를 사용하여 HTML을 파싱하고,
 * 모든 HTML 태그를 제거한 순수 텍스트만 반환합니다.
 *
 * **주요 사용 사례:**
 * - 마크다운/HTML 컨텐츠를 일반 텍스트로 표시할 때
 * - 검색 인덱싱용 텍스트 추출
 * - 텍스트 길이 제한 시 블록 요소 스타일 영향 제거
 *
 * @param html - 파싱할 HTML 문자열
 * @returns HTML 태그가 제거된 순수 텍스트
 *
 * @example
 * ```typescript
 * const html = '<p>Hello <strong>World</strong></p>';
 * const text = stripHtmlTags(html);
 * // 결과: 'Hello World'
 * ```
 *
 * @example
 * ```typescript
 * const html = '<blockquote><h1><em>Hi-Mold</em></h1></blockquote>';
 * const text = stripHtmlTags(html);
 * // 결과: 'Hi-Mold'
 * ```
 */
export const stripHtmlTags = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  } catch (error) {
    console.error('Failed to strip HTML tags:', error);
    return '';
  }
};

/**
 * HTML 문자열에서 첫 번째 블록 레벨 요소만 추출하고 필요시 잘라냅니다
 *
 * @description
 * DOMParser를 사용하여 HTML 문자열을 파싱하고,
 * body의 첫 번째 의미있는 컨텐츠를 반환합니다.
 * 긴 컨텐츠나 여러 줄은 자동으로 잘라서 미리보기만 표시합니다.
 *
 * **컨텐츠 잘라내기 규칙:**
 * 1. **줄 바꿈 (`<br>`)**: 첫 번째 `<br>` 이전까지만 표시
 * 2. **긴 텍스트**: 40자까지만 표시하고 "..." 추가
 * 3. **여러 블록**: 첫 번째 블록만 표시
 *
 * @param html - 파싱할 HTML 문자열
 * @returns 잘라낸 첫 번째 블록 요소와 추가 컨텐츠 존재 여부를 포함한 객체
 *
 * @example
 * ```typescript
 * const html = '<p>First paragraph</p><ul><li>Item 1</li></ul>';
 * const result = extractFirstHtmlBlock(html);
 * // result.firstBlock: '<p>First paragraph</p>'
 * // result.hasMore: true
 * ```
 *
 * @example
 * ```typescript
 * const html = '<p>첫 줄<br>두 번째 줄</p>';
 * const result = extractFirstHtmlBlock(html);
 * // result.firstBlock: '<p>첫 줄</p>'  (br 이전까지만)
 * // result.hasMore: true
 * ```
 *
 * @example
 * ```typescript
 * const html = '<p>긴 텍스트가 40자를 초과하면 잘립니다...</p>';
 * const result = extractFirstHtmlBlock(html);
 * // result.firstBlock: '<p>긴 텍스트가 40자를 초과하면 잘립니다......</p>'
 * // result.hasMore: true
 * ```
 */
export const extractFirstHtmlBlock = (html: string): HtmlBlockExtractionResult => {
  if (!html || typeof html !== 'string') {
    return { firstBlock: '', hasMore: false };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const blockLevelTags = new Set([
      'p',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'pre',
      'table',
      'section',
      'article',
      'aside',
      'header',
      'footer',
      'nav',
    ]);

    const bodyChildren = Array.from(doc.body.children).filter((child) =>
      blockLevelTags.has(child.tagName.toLowerCase())
    );
    const bodyChildNodes = Array.from(doc.body.childNodes);

    if (bodyChildren.length === 0) {
      const fullText = doc.body.textContent?.trim() || '';
      const hasLineBreaks = html.includes('<br>') || html.includes('<br/>');

      if (hasLineBreaks) {
        const brIndex = html.search(/<br\s*\/?>/i);
        if (brIndex !== -1) {
          const beforeBr = html.substring(0, brIndex).trim();
          return {
            firstBlock: `<p>${beforeBr}</p>`,
            hasMore: true,
          };
        }
      }

      if (fullText.length > MAX_BLOCK_LENGTH) {
        return {
          firstBlock: `<p>${fullText.substring(0, MAX_BLOCK_LENGTH)}...</p>`,
          hasMore: true,
        };
      }

      return { firstBlock: html.trim(), hasMore: false };
    }

    const firstBlockElement = bodyChildren[0];
    const firstElementIndex = bodyChildNodes.indexOf(firstBlockElement);

    const hasLeadingTextNode = bodyChildNodes.slice(0, firstElementIndex).some((node) => {
      const isTextNode = node.nodeType === Node.TEXT_NODE;
      const hasContent = (node.textContent?.trim().length ?? 0) > 0;
      return isTextNode && hasContent;
    });

    if (hasLeadingTextNode) {
      return { firstBlock: doc.body.innerHTML.trim(), hasMore: false };
    }

    const firstChild = firstBlockElement;
    const hasMultipleBlocks = bodyChildren.length > 1;

    const innerHTML = firstChild.innerHTML;
    const hasLineBreaks = innerHTML.includes('<br>') || innerHTML.includes('<br/>');

    let truncatedHTML = innerHTML;
    let hasMore = hasMultipleBlocks;

    if (hasLineBreaks) {
      const brIndex = innerHTML.search(/<br\s*\/?>/i);
      if (brIndex !== -1) {
        truncatedHTML = innerHTML.substring(0, brIndex);
        hasMore = true;
      }
    } else {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = innerHTML;
      const plainText = tempDiv.textContent || '';
      if (plainText.length > MAX_BLOCK_LENGTH) {
        truncatedHTML = plainText.substring(0, MAX_BLOCK_LENGTH) + '...';
        hasMore = true;
      }
    }

    const tagName = firstChild.tagName.toLowerCase();
    const firstBlockHTML = `<${tagName}>${truncatedHTML}</${tagName}>`;

    return {
      firstBlock: firstBlockHTML,
      hasMore,
    };
  } catch (error) {
    console.error('Failed to parse HTML:', error);
    return { firstBlock: html, hasMore: false };
  }
};
